"use client";

import React from "react";
import { Button, Input, Link } from "@heroui/react";
import { Icon } from "@iconify/react";
import { Formik, ErrorMessage, FormikHelpers } from "formik";
import * as Yup from "yup";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslations } from "../../contexts/translation.context";
import RightImage from './image_00006 1.jpg'
import { parseApiError } from "../../utils/errorParser";
import './index.css'
import { Typography, CustomCheckbox, AlertBlock } from "../../components";
import { buttonColors } from "../../theme/colors";
import { LogoPartnerIc } from "../../components/icons";
import { LanguageSwitcher } from "../../components/LanguageSwitcher";

type PartnerSignInFormValues = {
  email: string;
  password: string;
  remember: boolean;
};

const PARTNER_REMEMBER_KEY = 'partnerRememberMe';
const PARTNER_CREDENTIALS_KEY = 'partnerCredentials';
const LEGACY_REMEMBER_KEY = 'rememberMe';

const getStoredPartnerValues = (): PartnerSignInFormValues => {
  if (typeof window === 'undefined') {
    return { email: '', password: '', remember: false };
  }

  const rememberRaw = localStorage.getItem(PARTNER_REMEMBER_KEY) ?? localStorage.getItem(LEGACY_REMEMBER_KEY);
  const remember = rememberRaw === 'true';

  if (!remember) {
    return { email: '', password: '', remember: false };
  }

  const storedCredentials = localStorage.getItem(PARTNER_CREDENTIALS_KEY);
  if (storedCredentials) {
    try {
      const parsed = JSON.parse(storedCredentials) ?? {};
      return {
        email: typeof parsed.email === 'string' ? parsed.email : '',
        password: typeof parsed.password === 'string' ? parsed.password : '',
        remember: true,
      };
    } catch {
      localStorage.removeItem(PARTNER_CREDENTIALS_KEY);
    }
  }

  return { email: '', password: '', remember: true };
};

const persistPartnerValues = (values: PartnerSignInFormValues) => {
  if (typeof window === 'undefined') {
    return;
  }

  localStorage.removeItem(LEGACY_REMEMBER_KEY);

  if (values.remember) {
    localStorage.setItem(PARTNER_REMEMBER_KEY, 'true');
    localStorage.setItem(
      PARTNER_CREDENTIALS_KEY,
      JSON.stringify({
        email: values.email ?? '',
        password: values.password ?? '',
      })
    );
  } else {
    localStorage.setItem(PARTNER_REMEMBER_KEY, 'false');
    localStorage.removeItem(PARTNER_CREDENTIALS_KEY);
  }
};

// Validation schema for partner sign in - UPDATED v2
const getSignInSchema = (t: (key: string) => string) => {
  return Yup.object().shape({
    email: Yup.string()
      .email(t('auth.validation.emailInvalid'))
      .required(t('auth.validation.emailRequired')),
    password: Yup.string()
      .min(6, t('auth.validation.passwordMin'))
      .max(25, t('auth.validation.passwordMax'))
      .required(t('auth.validation.passwordRequired')),
    remember: Yup.boolean(),
  });
}
export default function PartnerSignIn() {
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [currentStep, setCurrentStep] = React.useState<'select' | 'signin'>('select');
  const [initialFormValues] = React.useState<PartnerSignInFormValues>(() => getStoredPartnerValues());
  const lastEnteredValuesRef = React.useRef<PartnerSignInFormValues>(initialFormValues);
  const updateRememberedValues = React.useCallback((nextValues: PartnerSignInFormValues) => {
    lastEnteredValuesRef.current = nextValues;
    persistPartnerValues(nextValues);
  }, []);

  const { login, loginWithToken, googleAuthPartner } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslations();

  // Авторизация по токену из query-параметра
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    if (token) {
      loginWithToken(token, 'partner')
        .then(() => {
          navigate('/partner/dashboard', { replace: true });
        })
        .catch((err) => {
          setError(t('auth.errors.tokenAuthError'));
        });
    }
    // eslint-disable-next-line
  }, [location.search]);
  
  // Check for success message from other pages and navigation state
  React.useEffect(() => {
    if (location.state?.message) {
      setSuccess(location.state.message);
      // Clear the message from location state
      navigate(location.pathname, { replace: true });
    }
    
    // If coming from client with skipSelect, skip the select step
    if (location.state?.skipSelect) {
      setCurrentStep('signin');
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate]);
  

  const handleSubmit = async (
    values: PartnerSignInFormValues,
    { setSubmitting, setValues }: FormikHelpers<PartnerSignInFormValues>
  ) => {
    const submittedValues = { ...values };
    lastEnteredValuesRef.current = submittedValues;
    try {
      setIsLoading(true);
      setError(null);
      
      await login(values.email, values.password, 'partner');
      
      // Redirect to partner dashboard (ignore previous location)
      navigate('/partner/dashboard', { replace: true });
    } catch (error: any) {
      console.error("Login failed:", error);
      setError(parseApiError(error, t('auth.errors.loginError')));
      setTimeout(() => {
        setValues(submittedValues, false);
      }, 0);
    } finally {
      updateRememberedValues(lastEnteredValuesRef.current);
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <div className="relative flex min-h-screen w-full items-center justify-center bg-white pl-[24px]">
      <div className="absolute top-12 left-12 flex  items-center">
        <LogoPartnerIc />
      </div>
      <div className="flex w-full min-h-[calc(100vh-48px) lg:flex-row lg:items-stretch">
        <div
          className={`flex w-full items-center justify-center bg-background px-6 py-12 lg:flex-1`}
        >
            <PartnerSignInFormStep
              t={t}
              error={error}
              success={success}
              isLoading={isLoading}
              initialValues={initialFormValues}
              onSubmit={handleSubmit}
              updateRememberedValues={updateRememberedValues}
              googleAuthPartner={googleAuthPartner}
            />

        </div>
        {/* <div className="hidden w-full items-stretch gap-[48px] lg:flex lg:flex-1"> */}
          {/* */}
          <div className="relative hidden w-full items-stretch gap-[48px] lg:flex lg:flex-1">

            <img
              src={RightImage}
              className="hidden object-cover h-screen p-[24px] w-full flex-1 !rounded-[64px] overflow-hidden  lg:block"

            />
          </div>

          {/* <div
            className="hidden h-full w-full flex-1 overflow-hidden rounded-[32px] lg:block"
            style={{
              backgroundImage: `url(${RightImage})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          /> */}
        {/* </div> */}
      </div>
    </div>
  );
}

type PartnerAuthSelectStepProps = {
  t: (key: string) => string;
  onCreatorSelect: () => void;
  onMarketerSelect: () => void;
  onNavigateSignUp: () => void;
};

type PartnerSignInFormStepProps = {
  t: (key: string) => string;
  error: string | null;
  success: string | null;
  isLoading: boolean;
  initialValues: PartnerSignInFormValues;
  onSubmit: (values: PartnerSignInFormValues, helpers: FormikHelpers<PartnerSignInFormValues>) => Promise<void>;
  updateRememberedValues: (values: PartnerSignInFormValues) => void;
  googleAuthPartner: () => void;
};

const PartnerSignInFormStep: React.FC<PartnerSignInFormStepProps> = React.memo(
  ({
    t,
    error,
    success,
    isLoading,
    initialValues,
    onSubmit,
    updateRememberedValues,
    googleAuthPartner,
  }) => {
    const [localIsVisible, setLocalIsVisible] = React.useState(false);

    const localToggleVisibility = () => setLocalIsVisible(!localIsVisible);

    return (
      <div className="flex w-[424px] flex-col items-center gap-[16px]">
        <div className="w-full flex flex-col text-center mb-[16px] gap-[8px]">
          <Typography variant="h3">
            Администратор
          </Typography>
          <Typography className="text-[#71717A]" variant="body1">
            Введите Ваши данные авторизации ниже
          </Typography>
        </div>

        {error && (
          <div className="w-full mb-[16px] bg-[#fee7ef] border-2 border-[#f7d0dd] rounded-[12px] p-[16px] shadow-sm">
            <div className="flex items-center gap-[16px]">
              <div className="w-[39px] h-[39px] bg-[#fee7ef] border border-[#f7d0dd] rounded-[30px] flex items-center justify-center flex-shrink-0">
                <Icon icon="mingcute:information-fill" width="19" height="19" className="text-[#c41552]" />
              </div>
              <p className="text-[#c41552] text-[14px] font-medium leading-[20px]">{error}</p>
            </div>
          </div>
        )}

        {success && (
          <AlertBlock
            type="rounded"
            data={[{ severity: "success", description: success }]}
            className="w-full mb-[16px]"
          />
        )}

        <Formik<PartnerSignInFormValues>
          key="partner-signin-form"
          initialValues={initialValues}
          validationSchema={getSignInSchema(t)}
          onSubmit={onSubmit}
          enableReinitialize={false}
        >
          {({ handleSubmit, isSubmitting, values, setFieldValue }) => (
            <form
              className="flex w-full flex-col gap-[16px]"
              onSubmit={handleSubmit}
              noValidate={true}
              lang="ru"
              data-disable-validation="true"
              autoComplete="off"
              onInvalid={(event: React.FormEvent<HTMLFormElement>) => event.preventDefault()}
            >
              <div className="flex flex-col gap-1 w-full">
                <Input
                  label={t('labels.email')}
                  name="email"
                  placeholder={t('placeholders.enterEmail')}
                  type="email"
                  variant="bordered"
                  value={values.email}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    if (event.nativeEvent?.isTrusted === false) {
                      return;
                    }
                    const nextEmail = event.target.value;
                    setFieldValue("email", nextEmail);
                    updateRememberedValues({
                      ...values,
                      email: nextEmail,
                    });
                  }}
                  labelPlacement="inside"
                  className="w-full custom-required-label-partner"
                  classNames={{
                    inputWrapper: "h-[56px]",
                    input: 'text-[#000]',
                  }}
                  autoComplete="email"
                  spellCheck="false"
                  data-no-validate="true"
                  inputMode="email"
                  onInvalid={(event: React.FormEvent<HTMLInputElement>) => event.preventDefault()}
                  onInput={(event: React.FormEvent<HTMLInputElement>) => {
                    (event.target as HTMLInputElement).setCustomValidity('');
                  }}
                  title=""
                  required={false}
                />
                <ErrorMessage name="email" component="div" className="text-red-500 text-xs" />
              </div>

              <div className="flex flex-col gap-1 w-full">
                <Input
                  endContent={
                    <button type="button" onClick={localToggleVisibility}>
                      {localIsVisible ? (
                        <Icon className="pointer-events-none text-2xl text-default-400" icon="solar:eye-closed-linear" />
                      ) : (
                        <Icon className="pointer-events-none text-2xl text-default-400" icon="solar:eye-bold" />
                      )}
                    </button>
                  }
                  label={t('labels.password')}
                  name="password"
                  placeholder={t('placeholders.enterPassword')}
                  type={localIsVisible ? "text" : "password"}
                  variant="bordered"
                  value={values.password}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    if (event.nativeEvent?.isTrusted === false) {
                      return;
                    }
                    const nextPassword = event.target.value;
                    setFieldValue("password", nextPassword);
                    updateRememberedValues({
                      ...values,
                      password: nextPassword,
                    });
                  }}
                  labelPlacement="inside"
                  className="w-full custom-required-label-partner"
                  classNames={{
                    inputWrapper: "h-[56px]",
                    input: 'text-[#000]',
                  }}
                  autoComplete="current-password"
                  spellCheck="false"
                  data-no-validate="true"
                  onInvalid={(event: React.FormEvent<HTMLInputElement>) => event.preventDefault()}
                  onInput={(event: React.FormEvent<HTMLInputElement>) => {
                    (event.target as HTMLInputElement).setCustomValidity('');
                  }}
                  pattern=".*"
                  title=""
                  required={false}
                />
                <ErrorMessage name="password" component="div" className="text-red-500 text-xs" />
              </div>

              <div className="flex w-full items-center justify-between px-1">
                <CustomCheckbox
                  name="remember"
                  size="sm"
                  isSelected={values.remember}
                  onValueChange={(checked: boolean) => {
                    const nextValues = {
                      ...values,
                      remember: checked,
                    };
                    setFieldValue("remember", checked);
                    updateRememberedValues(nextValues);
                  }}
                >
                  {t('auth.rememberMe')}
                </CustomCheckbox>
              </div>
              <Button
                className="w-full font-semibold h-[48px] mt-[8px] text-[16px]"
                type="submit"
                isLoading={isLoading || isSubmitting}
                disabled={isLoading || isSubmitting}
                style={{
                  backgroundColor: buttonColors.partner.primary.background,
                  color: buttonColors.partner.primary.text,
                }}
              >
                {isLoading || isSubmitting ? t('auth.signingIn') : t('auth.signIn')}
              </Button>
            </form>
          )}
        </Formik>
      </div>
    );
  }
);
